<script setup lang="ts">
import { computedAsync } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon, HashtagIcon } from '@heroicons/vue/20/solid'
import { CalendarDaysIcon, DocumentMinusIcon } from '@heroicons/vue/24/outline'
import Datetime from "../ui/inputs/Datetime.vue";
import AlertModal from '../ui/modals/AlertModal.vue';
import adminAPI from '@/services/api';
import { useClaimStore, useNotificationStore } from '@/store';
import Price from '../ui/inputs/Price.vue';
import { BoltSlashIcon, CurrencyEuroIcon, ReceiptRefundIcon, ScaleIcon } from '@heroicons/vue/24/solid';
import SelectClaimType from '../ui/select/SelectClaimType.vue';
import SelectCompensationAmount from '../ui/select/SelectCompensationAmount.vue';
import BaseInput from '@/components/ui/inputs/BaseInput.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import SelectCompetentCourt from '../ui/select/SelectCompetentCourt.vue';
import SearchAirline from '../search/SearchAirline.vue';
import { ChatBubbleLeftIcon } from '@heroicons/vue/24/solid';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/vue/24/solid';
import { ClaimFamilies, FamilyWithUsers, UsersClaimsWithUsers } from '@/interfaces';
import { groupBy, map } from 'ramda';

const props = defineProps<{
  open: boolean;
}>();
const emits = defineEmits(["close"]);

watch(() => props.open, (newValue) => open.value = newValue);

const open = ref(false);

/// Update Front Conversation form
const frontConversationForm = ref({
  isLoading: false,
  rootFrontConversationId: undefined as any,
});

async function updateFrontConversation() {
  frontConversationForm.value.isLoading = true;
  try {
    console.log(frontConversationForm.value)
    await adminAPI.updateClaim(useClaimStore().claim.id, {
      legalSpecification: {
        rootFrontConversationId: frontConversationForm.value.rootFrontConversationId,
      },
    });
    useNotificationStore().emitNotification({
      title: "Conversation Front re-définie avec succès",
      message: `Nouvel ID: ${frontConversationForm.value.rootFrontConversationId}`
    });
  } catch (error) {
    
  } finally {
    frontConversationForm.value.isLoading = false;
    await useClaimStore().refreshClaim();
  } 
}

/// Procedure form
const procedureForm = ref({
  isLoading: false,
  numeroRG: null as any,
  juridictionId: useClaimStore().claim.procedure?.juridictionId,
});

async function updateProcedure() {
  procedureForm.value.isLoading = true;
  try {
    console.log(procedureForm.value)
    await adminAPI.setProcedureIdentifier(useClaimStore().claim.id, {
      numeroRG: procedureForm.value.numeroRG,
      juridictionId: (procedureForm.value.juridictionId as any)?.value,
    });
    useNotificationStore().emitNotification({ title: "Procédure modifiée avec succès" });
  } catch (error) {
    
  } finally {
    procedureForm.value.isLoading = false;
    await useClaimStore().refreshClaim();
  } 
}

/// Schedule form
const updateEffectiveFlightForm = ref({
  scheduledDepartureLocal: null,
  actualDepartureLocal: null,
  scheduledArrivalLocal: null,
  inGateArrivalLocal: null,
  effectiveAirline: {
    iata: null as any as string,
    icao: null as any as string,
    id: null as any as string,
  }
});

const isLoading = ref(false);
async function updateSchedule() {
  isLoading.value = true;
  try {
    const { success } = await adminAPI.updateClaim(useClaimStore().claim?.id, {
      schedule: {
        scheduledDepartureLocal: updateEffectiveFlightForm.value.scheduledDepartureLocal as any,
        actualDepartureLocal: updateEffectiveFlightForm.value.actualDepartureLocal as any,
        scheduledArrivalLocal: updateEffectiveFlightForm.value.scheduledArrivalLocal as any,
        inGateArrivalLocal: updateEffectiveFlightForm.value.inGateArrivalLocal as any,
      },
      legalSpecification: {
        effectiveAirline: updateEffectiveFlightForm.value.effectiveAirline,
      }
    });
    if (success) {
      useNotificationStore().emitNotification({
        title: "Vol effectif mis à jour avec succès"
      })
      await useClaimStore().refreshClaim();
    }
  } finally {
    isLoading.value = false;
    await useClaimStore().refreshClaim()
  }

}

/// Legal specification form
const LegalSpecificationForm = ref({
  claimTypeName: null,
  ec261CompensationAmount: null,
  article700Amount: null,
  abusiveResistanceAmount: null,
  lackOfNoticeAmount: null,
  procedureCosts: null,
  usersClaims: [] as any[],
});
const updateLegalSpecificationResponse = ref();

function updateLegalSpecification() {
  const usersClaimsIds = useClaimStore().claim?.usersClaims.map(({ id }) => id);
  updateLegalSpecificationResponse.value = adminAPI.updateClaim(useClaimStore().claim?.id, {
    legalSpecification: {
      claimTypeName: LegalSpecificationForm.value.claimTypeName as any,
      ec261CompensationAmount: LegalSpecificationForm.value.ec261CompensationAmount as any,
      article700Amount: LegalSpecificationForm.value.article700Amount as any,
      abusiveResistanceAmount: LegalSpecificationForm.value.abusiveResistanceAmount as any,
      lackOfNoticeAmount: LegalSpecificationForm.value.lackOfNoticeAmount as any,
      procedureCosts: LegalSpecificationForm.value.procedureCosts as any,
      usersClaims: LegalSpecificationForm.value.usersClaims.map(
        (data, i) => ({...data.toJSON(), userClaimId: usersClaimsIds[i] })
      ).filter(Boolean),
    }
  });
  watch(updateLegalSpecificationResponse.value, (value) => {
    if (value.data?.success === true) {
      useNotificationStore().emitNotification({
        title: "Demande mise à jour, merci de rafraichir la page"
      })
    }
  });
}

/// Families
const claimFamilies = ref<ClaimFamilies>();
watch(() => useClaimStore().claim.id, async (claimId) => {
  claimFamilies.value = await adminAPI.listClaimFamilies(claimId)
}, { immediate: true });

const drag = ref(false);
const hasFamilyChange = ref(false);
const transitionOptions = computed(() => { return { animation: 200, disabled: false, ghostClass: "ghost", }; });

async function syncFamilyChanges() {
  if (!claimFamilies?.value?.length) {
    useNotificationStore().emitNotification({ title: "Aucune modification detectée" })
    return;
  }

  await adminAPI.updateClaimFamilies(useClaimStore().claim.id, {
    families: claimFamilies?.value.map(cf => {
      return {
        familyId: cf.id,
        usersClaimsIds: cf.usersClaims?.map(({id}) => id) || []
      }
    })
  });

  useNotificationStore().emitNotification({ title: "Structure familiale(s) modifiée(s) avec succès" });
  hasFamilyChange.value = false;
  await useClaimStore().refreshClaim();
}

async function extractInNewFamily(userClaimId: string) {
  await adminAPI.extractInNewFamily(useClaimStore().claim.id, userClaimId);
  useNotificationStore().emitNotification({ title: "Nouvelle famille créée avec succès", type: "success" });
  await useClaimStore().refreshClaim();
}

function close() {
  useClaimStore().retrieveClaim(useClaimStore().claim?.id)
  open.value = false;
  emits("close");
}
</script>
<template>
<div>
  <AlertModal
    type="info"
    title="Modifier la claim"
    message="Formulaire pour modifier le dossier en cours"
    confirm-message="" 
    :open=open
    @close="close"
    @open="open = true"
    width="max-w-4xl"
  >
    <div class="w-full px-4">
      <div class="w-full p-2 mx-auto bg-white rounded-2xl">
        <!-- NUMERO RG -->
        <Disclosure :defaultOpen="true" v-if="useClaimStore().claim.claimLegalStep.name === 'COURT'" v-slot="{ open }">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-indigo-900 bg-indigo-100 rounded-lg hover:bg-indigo-200 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75"
          >
            <span>Procédure contentieuse</span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-indigo-500"
            />
          </DisclosureButton>
          <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
            <form>
              <div class="grid grid-cols-2 gap-4 py-6">
                <BaseInput label="Numéro RG" :icon="HashtagIcon" v-model="procedureForm.numeroRG" :value="useClaimStore().claim.procedure?.identifier" />
                <SelectCompetentCourt :claim-id="useClaimStore().claim.id" v-model="procedureForm.juridictionId" />
              </div>
              <div class="flex justify-end py-4">
                <BaseButton text="Modifier la procédure" @click="updateProcedure" type="submit" size="lg" :loading="procedureForm.isLoading" />
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>
        
        <!-- FRONT -->
        <Disclosure :defaultOpen="true" v-if="useClaimStore().claim.claimLegalStep.name !== 'CLAIM_VALIDATION'" v-slot="{ open }">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-indigo-900 bg-indigo-100 rounded-lg hover:bg-indigo-200 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75"
          >
            <span>Conversation Front</span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-indigo-500"
            />
          </DisclosureButton>
          <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
            <form>
              <div class="grid grid-cols-2 gap-4 py-6">
                <BaseInput label="Front Conversation ID" :icon="ChatBubbleLeftEllipsisIcon" v-model="frontConversationForm.rootFrontConversationId" :value="useClaimStore().claim.conversation?.frontConversationId"
                  description="Redéfinir la conversation Front principale"
                 />
              </div>
              <div class="flex justify-end py-4">
                <BaseButton text="Redéfinir la conversation" @click="updateFrontConversation" type="submit" :loading="frontConversationForm.isLoading" />
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>

        <!-- FLIGHT -->
        <Disclosure :defaultOpen="true" v-slot="{ open }">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-indigo-900 bg-indigo-100 rounded-lg hover:bg-indigo-200 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75"
          >
            <span>Modifier le vol & ses horaires</span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-indigo-500"
            />
          </DisclosureButton>
          <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
            <form>
              <div>
                <SearchAirline @update:airline="(e) => updateEffectiveFlightForm.effectiveAirline.id = e.id" label="Re-définir la compagnie effective">
                  <template #label>
                    <label class="block text-sm font-medium leading-6 text-left text-gray-900">
                      Re-définir la compagnie effective
                    </label>
                  </template>
                </SearchAirline>
                <p class="mt-2 text-sm text-slate-600">La companie effective actuelle est :
                  <span class="font-semibold text-slate-700">{{ useClaimStore().claim.legalSpecification.effectiveAirline.name }}</span>
                  (IATA:  <span class="font-semibold text-slate-700">{{ useClaimStore().claim.legalSpecification.effectiveAirline.iata }}</span>  /  ICAO:  <span class="font-semibold text-slate-700">{{ useClaimStore().claim.legalSpecification.effectiveAirline.icao }})</span>
                </p>
              </div>
              <div class="grid grid-cols-2 gap-4 py-6">
                <Datetime
                  label="Horaire de départ prévu"
                  description="Exemple: 25/07/2023 19:00"
                  :icon="CalendarDaysIcon"
                  tooltip="Horaires locales, comme indiqué sur le billet"
                  v-model="updateEffectiveFlightForm.scheduledDepartureLocal"
                />
                <Datetime
                  label="Horaire de départ réel"
                  description="Exemple: 25/07/2023 23:00"
                  :icon="CalendarDaysIcon"
                  tooltip="Laisser vide si inconnu"
                  v-model="updateEffectiveFlightForm.actualDepartureLocal"
                />
                <Datetime
                  label="Horaire d'arrivée prévu"
                  description="Exemple: 26/07/2023 07:00"
                  :icon="CalendarDaysIcon"
                  tooltip="Horaires locales, comme indiqué sur le billet"
                  v-model="updateEffectiveFlightForm.scheduledArrivalLocal"
                />
                <Datetime
                  label="Horaire d'arrivée réel"
                  description="Exemple: 26/07/2023 10:30"
                  :icon="CalendarDaysIcon"
                  tooltip="Laisser vide si inconnu"
                  v-model="updateEffectiveFlightForm.inGateArrivalLocal"
                />
              </div>
              <div class="flex justify-end py-4">
                <BaseButton type="submit" text="Modifier le vol effectif" @click="updateSchedule" :loading="isLoading" />
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>

        <!-- FAMILY -->
        <Disclosure v-if="claimFamilies" :defaultOpen="true" as="div" class="mt-2" v-slot="{ open }">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-teal-900 bg-teal-100 rounded-lg hover:bg-teal-200 focus:outline-none focus-visible:ring focus-visible:ring-teal-500 focus-visible:ring-opacity-75"
          >
            <span>Modifier les familles</span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-teal-500"
            />
          </DisclosureButton>
          <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
            <form>
              <div class="flow-root my-8">
                <div class="-my-2 overflow-x-auto">
                  <div class="inline-block min-w-full py-1 align-middle border rounded-md shadow-md border-zinc-300">
                    <div class="min-w-full">
                      <!-- Header -->
                      <div class="bg-white flex py-3.5 text-sm font-semibold text-gray-900">
                        <div class="flex-1 pl-4 pr-3 sm:pl-3">Nom complet</div>
                        <div class="flex-1 px-3">Date de Naissance</div>
                        <div class="flex-1 px-3">Email</div>
                        <!-- <div class="w-20 pl-3 pr-4 sm:pr-3">
                          <span class="sr-only">Edit</span>
                        </div> -->
                      </div>

                      <!-- Content -->
                      <div class="bg-white">
                        <template v-for="family in claimFamilies" :key="family.familyName">
                          <!-- Family group header -->
                          <div class="py-2 pl-4 pr-3 text-sm italic text-left border-t border-gray-200 text-stone-600 bg-gray-50 sm:pl-3">
                            Famille {{ family.familyName }} ({{ family.usersClaims?.length || 'Aucun Passager' }})
                          </div>

                          <!-- Draggable group -->
                          <draggable v-if="family.usersClaims?.length"
                            group="passengers-families" 
                            :list="family.usersClaims"
                            item-key="id" 
                            tag="div" 
                            role="list"
                            v-bind="transitionOptions"
                            @start="drag = true"
                            @end="drag = false"
                            @change="hasFamilyChange = true"
                          >
                            <template #item="{ element: userClaim, index }: { element: UsersClaimsWithUsers[number], index: number }">
                              <div 
                                class="flex items-center py-4 text-sm bg-transparent border-t cursor-pointer"
                                :class="[index === 0 ? 'border-gray-300' : 'border-gray-200']"
                              >
                                <div class="flex-1 pl-4 pr-3 font-medium text-gray-900 sm:pl-3">{{ userClaim.user.firstName }} {{ userClaim.user.lastName.toUpperCase() }}</div>
                                <div class="flex-1 px-3 text-gray-500">{{ userClaim.user.birthdate }}</div>
                                <div class="flex-1 px-3 text-gray-500">{{ userClaim.email || userClaim.user.email }}</div>
                                <div v-if="family.usersClaims?.length > 1" class="w-20 pl-3 pr-4 font-medium text-right sm:pr-3">
                                  <a @click="extractInNewFamily(userClaim.id)" class="text-indigo-600 hover:text-indigo-900">
                                    Nouvelle famille
                                  </a>
                                </div>
                              </div>
                            </template>
                          </draggable>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="hasFamilyChange" class="flex justify-end py-4">
                <BaseButton type="submit" text="Modifier les familles" @click="syncFamilyChanges" :loading="isLoading" />
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>

        <!-- LEGAL -->
        <Disclosure :defaultOpen="true" as="div" class="mt-2" v-slot="{ open }">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-indigo-900 bg-indigo-100 rounded-lg hover:bg-indigo-200 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75"
          >
            <span>Modifier la demande</span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-indigo-500"
            />
          </DisclosureButton>
          <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
            <form>
              <div class="grid grid-cols-2 gap-4 py-6">
                <SelectClaimType v-model="LegalSpecificationForm.claimTypeName" />
                <SelectCompensationAmount v-model="LegalSpecificationForm.ec261CompensationAmount" />
                <!-- <Price label="Montant Article-700 demandé" v-model="LegalSpecificationForm.article700Amount" :default="useClaimStore().claim.legalSpecification.article700Amount" description="Exemple: 1500€" :icon="ScaleIcon" /> -->
                <!-- <Price label="Montant résistance abusive demandé" v-model="LegalSpecificationForm.abusiveResistanceAmount" :default="useClaimStore().claim.legalSpecification.abusiveResistanceAmount" description="Exemple: 750€" :icon="BoltSlashIcon" /> -->
                <!-- <Price label="Montant défaut de notice d'information demandé" v-model="LegalSpecificationForm.lackOfNoticeAmount" :default="useClaimStore().claim.legalSpecification.lackOfNoticeAmount" description="Exemple: 200€" :icon="CurrencyEuroIcon" /> -->
                <!-- <Price label="Montant des dépens engagés" v-model="LegalSpecificationForm.procedureCosts" :default="useClaimStore().claim.legalSpecification.procedureCosts" description="Exemple: 110,76" :icon="ReceiptRefundIcon" /> -->
              </div>
              <div class="mt-6">
                <h3 class="text-base font-medium leading-7 text-gray-900">Modifier les demandes individuelles :</h3>
                <div class="grid grid-cols-4 gap-4 py-6">
                  <div v-for="(userClaim, idx) in useClaimStore().claim?.usersClaims" class="divide-y-2 ">
                    {{ userClaim.user.firstName }} {{ userClaim.user.lastName.toUpperCase() }}
                    <Price v-model="LegalSpecificationForm.usersClaims[idx]" label="Montant Convention de Montréal" :icon="ReceiptRefundIcon" />
                  </div>
                </div>
              </div>
              <div class="flex justify-end py-4">
                <button type="submit"
                  @click.prevent="updateLegalSpecification"
                  class="inline-flex px-3 py-2 font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                  <span v-if="updateLegalSpecificationResponse?.isLoading" class="w-5 h-5 mr-3 -ml-1">
                    <svg role="status" class="inline w-4 h-4 mx-2 text-blue-200 animate-spin"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="#1C64F2" />
                    </svg>
                  </span>
                  Modifier la demande
                </button>
              </div>
            </form>
          </DisclosurePanel>
        </Disclosure>
      </div>
    </div>
  </AlertModal>
</div>
</template>
